import { Check, Plus, Warning, X } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import { BalancesResponse, Entity } from '../../../../../../../types/apiTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../../utils/UserContext';
import { Select, InputNumber, Slider } from 'antd';
import {
  InternalBankAccount,
  LinkedAccount,
} from '../../../../../../../types/dbTypes';
import { usePlaidLink } from 'react-plaid-link';
import { formatNumberCommas } from '../../../../../../../utils/CurrencyUtils';
import { delay } from '../../../../../../../utils/Delay';
import WithdrawalSuccess from './WithdrawalSuccess';
import { LinkMethodChooserModal } from '../../linkAccount/LinkMethodChooserModal';
import { ManualLinkModal } from '../../linkAccount/ManualLinkModal';
import { LinkMethod } from '../../linkAccount/types';

export type WithdrawFormData = {
  linkedAccountId: string;
  internalBankAccountId: string;
  transferAmount: number;
};

export const WithdrawModal = ({
  isOpen,
  onClose,
  balances,
  accountInfo,
}: {
  isOpen: boolean;
  onClose: () => void;
  balances: BalancesResponse;
  accountInfo: InternalBankAccount;
}) => {
  const { handleSubmit, control, watch } = useForm<WithdrawFormData>({
    defaultValues: {
      linkedAccountId: '',
      transferAmount: 0,
    },
  });
  const { userRecord, userId } = useUser();
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const [loadingToken, setLoadingToken] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const hasNoLinkedAccounts = !loadingAccounts && linkedAccounts.length === 0;
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [openLink, setOpenLink] = useState(false);
  const [linkMethodChooserOpen, setLinkMethodChooserOpen] = useState(false);
  const [manualLinkOpen, setManualLinkOpen] = useState(false);
  const [withdrawSuccessPopupVisible, setWithdrawSuccessPopupVisible] =
    useState(false);

  const currentAccount = useMemo(
    () => linkedAccounts?.find((link) => link.account_id === selectedAccount),
    [linkedAccounts, selectedAccount]
  );

  const onSubmit = (data: WithdrawFormData) => {
    const recordInfo = {
      linkedAccountId: currentAccount?.account_id ?? '',
      internalBankAccountId: accountInfo.account_id,
      transferAmount: Math.round(data.transferAmount * 100), // cents as int
    };

    withdrawFunds(recordInfo);
  };

  const transferAmount = watch('transferAmount');
  const maxWithdraw = Math.max(0, Number(balances?.budgetedBalance || 0));
  const maxWithdrawLimit = Math.max(0, Number(balances?.cashAdvanceLimit || 0));

  const fetchLinkedAccounts = async (customer_id: string) => {
    setLoadingAccounts(true);
    try {
      const response = await callProtectedApi(
        `/linkedAccounts/list/customer/${customer_id}`,
        'get',
        {},
        'Error occurred while fetching external linked accounts.'
      );
      const data = response?.data?.data?.linkedAccounts;
      // An empty list is a normal answer, not a failure: customers can be activated without linking
      // a bank (onboarding no longer requires Plaid Link). The modal shows an empty state for it.
      if (Array.isArray(data) && response?.status === HttpStatusCode.Ok) {
        const sorted = data.sort(
          (a: { is_primary: any }, b: { is_primary: any }) =>
            (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)
        );
        setLinkedAccounts(sorted);

        // Auto-select the primary account
        const primary = sorted.find((a: any) => a.is_primary);
        if (primary) {
          setSelectedAccount(primary.account_id);
        } else {
          // fallback: select the first one if no primary exists
          setSelectedAccount(sorted[0]?.account_id ?? '');
        }
      } else {
        toast.error('Failed to fetch linked accounts.');
      }
    } catch (e: any) {
      console.error('Error occurred while fetching linked accounts: ', e);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const createLinkedAccount = async (
    customer_id: string,
    public_token: string
  ) => {
    setLoadingAccounts(true);
    try {
      const response = await callProtectedApi(
        `/linkedAccounts/${customer_id}`,
        'post',
        { entity: 'customer', public_token: public_token },
        'Error occurred while creating newly linked account record.'
      );
      if (response?.status === HttpStatusCode.Created) {
        await fetchLinkedAccounts(userId ?? '');
      } else {
        toast.error('Failed to save newly linked account.');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating newly linked account record: ',
        e
      );
    }
  };

  useEffect(() => {
    fetchLinkedAccounts(userId ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchLinkToken = async (customer_id: string) => {
      setLoadingToken(true);
      try {
        const body = {
          owner_id: customer_id,
          entity: 'customer', // Get curr (Customer, Vendor, or Partner)
        };
        const response = await callProtectedApi(
          '/linkedAccounts/plaidLinkToken',
          'POST',
          body,
          'Error occurred while connecting to Plaid.',
          {},
          undefined,
          5
        );
        if (response?.data?.data?.token) {
          setLinkToken(response.data.data.token);
        }
      } catch (error: any) {
        console.error('Error fetching Plaid link token:', error);
      } finally {
        setLoadingToken(false);
      }
    };
    fetchLinkToken(
      userRecord?.userType === Entity.Customer ? userRecord?.customer_id : ''
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSuccess = useCallback(
    async (public_token: string) => {
      try {
        await createLinkedAccount(userId ?? '', public_token);
        return;
      } catch (error: any) {
        console.error('Error adding payment method:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callProtectedApi]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken!,
    onSuccess,
  });

  useEffect(() => {
    if (openLink && ready) {
      open();
      setOpenLink(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLink, ready, open]);

  const withdrawFunds = async (recordInfo: WithdrawFormData) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/linkedAccounts/withdraw',
        'post',
        {
          customer_id: userId,
          linked_account_id: recordInfo?.linkedAccountId,
          internal_bank_account_id: recordInfo?.internalBankAccountId,
          amount: recordInfo?.transferAmount,
        },
        'Error occurred while initiating withdrawal.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        setWithdrawSuccessPopupVisible(true);
        // Surface the withdrawal in the "Processing" band immediately. Prefix-match so it fires
        // regardless of how the band resolves the customer id (the portal holds one customer).
        // Fire-and-forget (`void`) — the success popup is already shown, so the button's loading
        // state shouldn't wait on the band's refetch (matches the deposit mutation's onSuccess).
        void queryClient.invalidateQueries({
          queryKey: ['pendingTransactions'],
        });
      }
    } catch (e: any) {
      console.error('Error occurred while intiating withdrawal request: ', e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value: string) => {
    setSelectedAccount(value);
  };

  const clampAmount = (v: number) => {
    const n = Number.isFinite(v) ? v : 0;
    // keep two decimals and clamp to [0, maxWithdraw]
    return Math.min(maxWithdraw, Math.max(0, Number(n.toFixed(2))));
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-full max-w-[520px] overflow-x-hidden rounded-[4px] bg-white">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Withdraw
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              {!loadingAccounts ? (
                <form
                  className="flex h-full w-full flex-col gap-3 overflow-y-auto overflow-x-hidden border-[1px] border-bordergray p-6"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="flex w-full flex-row items-center space-x-4 align-middle">
                    <div className="flex w-full flex-col rounded-[4px] border-[1px] border-bordergray p-3">
                      <p>Total balance:</p>
                      <h2 className="text-[20px] font-[700] text-primary">
                        $
                        {formatNumberCommas(
                          Math.floor(balances?.budgetedBalance)
                        )}
                        <span className="text-[18px] leading-[28px] text-graysecondary">
                          .
                          {(balances?.budgetedBalance % 1 || 0)
                            .toFixed(2)
                            .split('.')[1] || '00'}
                        </span>
                      </h2>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Choose Bank Account
                    </label>
                    <Select
                      value={selectedAccount || undefined}
                      placeholder="No linked bank accounts"
                      onChange={handleChange}
                      options={linkedAccounts?.map((account) => ({
                        value: account.account_id,
                        // Label shows in both the dropdown and the closed selector
                        label: (
                          <div className="flex w-full flex-row items-center justify-between">
                            <span>
                              {account.bank_name} ****{account.bank_mask}
                            </span>
                            {account.is_primary && (
                              <span className="flex items-center gap-1 text-green-600">
                                <Check size={16} />
                                <span className="text-[12px] font-semibold">
                                  Primary
                                </span>
                              </span>
                            )}
                          </div>
                        ),
                      }))}
                      className="w-full min-w-full"
                    />
                    {hasNoLinkedAccounts && (
                      <p className="mt-1 text-xs text-gray-500">
                        Link a bank account below to withdraw.
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setLinkMethodChooserOpen(true)}
                    type="button"
                    className="flex h-[32px] w-full flex-row items-center justify-center space-x-2 rounded-full border-[1px] border-[#212529] border-opacity-[8%] px-3 py-[6px] align-middle disabled:opacity-50"
                  >
                    <Plus />
                    <p>Link New Bank Account</p>
                    {loadingToken && (
                      <ClipLoader
                        color="#1D213B"
                        loading={loadingToken}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                        className="h-full w-full justify-center"
                      />
                    )}
                  </button>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Transfer Amount
                    </label>
                    <Controller
                      name="transferAmount"
                      control={control}
                      rules={{
                        required: 'Transfer amount is required',
                        min: {
                          value: 1,
                          message: 'Transfer amount must be greater than 0',
                        },
                      }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <div className="flex w-full flex-col items-start justify-between align-middle">
                          <Slider
                            styles={{
                              track: {
                                background: '#F7A826',
                              },
                            }}
                            min={0}
                            max={maxWithdraw}
                            onChange={(val) =>
                              onChange(clampAmount(Number(val)))
                            }
                            value={typeof value === 'number' ? value : 0}
                            step={0.01}
                            className="w-full"
                          />
                          <InputNumber
                            min={0}
                            step={0.01}
                            max={maxWithdraw}
                            value={value}
                            onChange={(val) =>
                              onChange(clampAmount(Number(val)))
                            }
                            formatter={(value) => `$${value}`}
                            parser={(value) =>
                              parseFloat(value?.replace('$', '') || '')
                            }
                            onBlur={onBlur}
                            className="w-full"
                          />
                        </div>
                      )}
                    />
                  </div>
                  {transferAmount > maxWithdrawLimit && (
                    <div className="flex w-full flex-row items-center space-x-2 align-middle text-[14px] text-[#E2660C]">
                      <Warning size={24} />
                      <p>
                        Warning: You have exceeded your take-home funds. This
                        transfer will include funds from your monthly budget.
                      </p>
                    </div>
                  )}
                </form>
              ) : (
                <div className="flex h-full w-full items-center justify-center align-middle">
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingAccounts}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                </div>
              )}
              {/* Buttons outside the form */}
              <div className="flex justify-between p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px]"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  disabled={
                    !selectedAccount ||
                    selectedAccount.length === 0 ||
                    transferAmount <= 0
                  }
                  onClick={handleSubmit(onSubmit)} // Use handleSubmit to trigger form submission
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600"
                >
                  {!loading ? (
                    'Withdraw'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
      case 'tablet':
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-full max-w-[520px] overflow-x-hidden rounded-[4px] bg-white">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Withdraw
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              {!loadingAccounts ? (
                <form
                  className="flex h-full w-full flex-col gap-3 overflow-y-auto overflow-x-hidden border-[1px] border-bordergray p-6"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="flex w-full flex-row items-center space-x-4 align-middle">
                    <div className="flex w-full flex-col rounded-[4px] border-[1px] border-bordergray p-3">
                      <p>Total balance:</p>
                      <h2 className="text-[20px] font-[700] text-primary">
                        $
                        {formatNumberCommas(
                          Math.floor(balances?.budgetedBalance)
                        )}
                        <span className="text-[18px] leading-[28px] text-graysecondary">
                          .
                          {(balances?.budgetedBalance % 1 || 0)
                            .toFixed(2)
                            .split('.')[1] || '00'}
                        </span>
                      </h2>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Choose Bank Account
                    </label>
                    <Select
                      value={selectedAccount || undefined}
                      placeholder="No linked bank accounts"
                      onChange={handleChange}
                      options={linkedAccounts?.map((account) => ({
                        value: account.account_id,
                        // Label shows in both the dropdown and the closed selector
                        label: (
                          <div className="flex w-full flex-row items-center justify-between">
                            <span>
                              {account.bank_name} ****{account.bank_mask}
                            </span>
                            {account.is_primary && (
                              <span className="flex items-center gap-1 text-green-600">
                                <Check size={16} />
                                <span className="text-[12px] font-semibold">
                                  Primary
                                </span>
                              </span>
                            )}
                          </div>
                        ),
                      }))}
                      className="w-full min-w-full"
                    />
                    {hasNoLinkedAccounts && (
                      <p className="mt-1 text-xs text-gray-500">
                        Link a bank account below to withdraw.
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setLinkMethodChooserOpen(true)}
                    type="button"
                    className="flex h-[32px] w-full flex-row items-center justify-center space-x-2 rounded-full border-[1px] border-[#212529] border-opacity-[8%] px-3 py-[6px] align-middle disabled:opacity-50"
                  >
                    <Plus />
                    <p>Link New Bank Account</p>
                    {loadingToken && (
                      <ClipLoader
                        color="#1D213B"
                        loading={loadingToken}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                        className="h-full w-full justify-center"
                      />
                    )}
                  </button>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Transfer Amount
                    </label>
                    <Controller
                      name="transferAmount"
                      control={control}
                      rules={{
                        required: 'Transfer amount is required',
                        min: {
                          value: 1,
                          message: 'Transfer amount must be greater than 0',
                        },
                      }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <div className="flex w-full flex-col items-start justify-between align-middle">
                          <Slider
                            styles={{
                              track: {
                                background: '#F7A826',
                              },
                            }}
                            min={0}
                            max={maxWithdraw}
                            onChange={(val) =>
                              onChange(clampAmount(Number(val)))
                            }
                            value={typeof value === 'number' ? value : 0}
                            step={0.01}
                            className="w-full"
                          />
                          <InputNumber
                            min={0}
                            step={0.01}
                            max={maxWithdraw}
                            value={value}
                            onChange={(val) =>
                              onChange(clampAmount(Number(val)))
                            }
                            formatter={(value) => `$${value}`}
                            parser={(value) =>
                              parseFloat(value?.replace('$', '') || '')
                            }
                            onBlur={onBlur}
                            className="w-full"
                          />
                        </div>
                      )}
                    />
                  </div>
                  {transferAmount > maxWithdrawLimit && (
                    <div className="flex w-full flex-row items-center space-x-2 align-middle text-[14px] text-[#E2660C]">
                      <Warning size={24} />
                      <p>
                        Warning: You have exceeded your take-home funds. This
                        transfer will include funds from your monthly budget.
                      </p>
                    </div>
                  )}
                </form>
              ) : (
                <div className="flex h-full w-full items-center justify-center align-middle">
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingAccounts}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                </div>
              )}
              {/* Buttons outside the form */}
              <div className="flex justify-between p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px]"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  disabled={
                    !selectedAccount ||
                    selectedAccount.length === 0 ||
                    transferAmount <= 0
                  }
                  onClick={handleSubmit(onSubmit)} // Use handleSubmit to trigger form submission
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600"
                >
                  {!loading ? (
                    'Withdraw'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
      case 'mobile':
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-[95%] max-w-[520px] overflow-x-hidden rounded-[4px] bg-white">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Withdraw
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              {!loadingAccounts ? (
                <form
                  className="flex h-full w-full flex-col gap-3 overflow-y-auto overflow-x-hidden border-[1px] border-bordergray p-6"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="flex w-full flex-row items-center space-x-4 align-middle">
                    <div className="flex w-full flex-col rounded-[4px] border-[1px] border-bordergray p-3">
                      <p>Total balance:</p>
                      <h2 className="text-[20px] font-[700] text-primary">
                        $
                        {formatNumberCommas(
                          Math.floor(balances?.budgetedBalance)
                        )}
                        <span className="text-[18px] leading-[28px] text-graysecondary">
                          .
                          {(balances?.budgetedBalance % 1 || 0)
                            .toFixed(2)
                            .split('.')[1] || '00'}
                        </span>
                      </h2>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Choose Bank Account
                    </label>
                    <Select
                      value={selectedAccount || undefined}
                      placeholder="No linked bank accounts"
                      onChange={handleChange}
                      options={linkedAccounts?.map((account) => ({
                        value: account.account_id,
                        // Label shows in both the dropdown and the closed selector
                        label: (
                          <div className="flex w-full flex-row items-center justify-between">
                            <span>
                              {account.bank_name} ****{account.bank_mask}
                            </span>
                            {account.is_primary && (
                              <span className="flex items-center gap-1 text-green-600">
                                <Check size={16} />
                                <span className="text-[12px] font-semibold">
                                  Primary
                                </span>
                              </span>
                            )}
                          </div>
                        ),
                      }))}
                      className="w-full min-w-full"
                    />
                    {hasNoLinkedAccounts && (
                      <p className="mt-1 text-xs text-gray-500">
                        Link a bank account below to withdraw.
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setLinkMethodChooserOpen(true)}
                    type="button"
                    className="flex h-[32px] w-full flex-row items-center justify-center space-x-2 rounded-full border-[1px] border-[#212529] border-opacity-[8%] px-3 py-[6px] align-middle disabled:opacity-50"
                  >
                    <Plus />
                    <p>Link New Bank Account</p>
                    {loadingToken && (
                      <ClipLoader
                        color="#1D213B"
                        loading={loadingToken}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                        className="h-full w-full justify-center"
                      />
                    )}
                  </button>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Transfer Amount
                    </label>
                    <Controller
                      name="transferAmount"
                      control={control}
                      rules={{
                        required: 'Transfer amount is required',
                        min: {
                          value: 1,
                          message: 'Transfer amount must be greater than 0',
                        },
                      }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <div className="flex w-full flex-col items-start justify-between align-middle">
                          <Slider
                            styles={{
                              track: {
                                background: '#F7A826',
                              },
                            }}
                            min={0}
                            max={maxWithdraw}
                            onChange={(val) =>
                              onChange(clampAmount(Number(val)))
                            }
                            value={typeof value === 'number' ? value : 0}
                            step={0.01}
                            className="w-full"
                          />
                          <InputNumber
                            min={0}
                            step={0.01}
                            max={maxWithdraw}
                            value={value}
                            onChange={(val) =>
                              onChange(clampAmount(Number(val)))
                            }
                            formatter={(value) => `$${value}`}
                            parser={(value) =>
                              parseFloat(value?.replace('$', '') || '')
                            }
                            onBlur={onBlur}
                            className="w-full"
                          />
                        </div>
                      )}
                    />
                  </div>
                  {transferAmount > maxWithdrawLimit && (
                    <div className="flex w-full flex-row items-center space-x-2 align-middle text-[14px] text-[#E2660C]">
                      <Warning size={24} />
                      <p>
                        Warning: You have exceeded your take-home funds. This
                        transfer will include funds from your monthly budget.
                      </p>
                    </div>
                  )}
                </form>
              ) : (
                <div className="flex h-full w-full items-center justify-center align-middle">
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingAccounts}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                </div>
              )}
              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-2 p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px]"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  disabled={
                    !selectedAccount ||
                    selectedAccount.length === 0 ||
                    transferAmount <= 0
                  }
                  onClick={handleSubmit(onSubmit)} // Use handleSubmit to trigger form submission
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600"
                >
                  {!loading ? (
                    'Withdraw'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  const handleWithdrawSuccessClose = async () => {
    onClose();
    await delay(500);
    window.location.reload();
  };

  if (!isOpen) return null;

  if (withdrawSuccessPopupVisible)
    return <WithdrawalSuccess onClose={handleWithdrawSuccessClose} />;

  return (
    <>
      {renderScreenView(screenView)}
      <LinkMethodChooserModal
        isOpen={linkMethodChooserOpen}
        onClose={() => setLinkMethodChooserOpen(false)}
        plaidReady={!!linkToken}
        onSelectMethod={(method) => {
          setLinkMethodChooserOpen(false);
          if (method === LinkMethod.Plaid) {
            setOpenLink(true);
          } else {
            setManualLinkOpen(true);
          }
        }}
      />
      <ManualLinkModal
        isOpen={manualLinkOpen}
        onClose={() => setManualLinkOpen(false)}
        ownerId={userId ?? ''}
        entity={Entity.Customer}
        onSuccess={() => fetchLinkedAccounts(userId ?? '')}
      />
    </>
  );
};

export default WithdrawModal;
